import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMutation, useReactiveVar } from '@apollo/client'
import Popover from 'react-bootstrap/Popover'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import { numberToFixed } from 'utils/number'
import { PricingTypes } from 'utils/constants'
import { timestampVar } from 'config/apollo/competitor-pricing/variables'
import { ReactComponent as PlusIcon } from 'images/competitor-pricing/cash-credit-differential/plus.svg'
import { ReactComponent as MinusIcon } from 'images/competitor-pricing/cash-credit-differential/minus.svg'
import { Toggle } from 'components/common/toggle'
import { determineDifferentialStep } from 'components/station/components/competitor-pricing/utils/helpers'
import { usePriceChangeDispatch } from 'components/station/components/competitor-pricing/context/priceChangeContext'
import { PriceChangeActions } from 'components/station/components/competitor-pricing/context/constants'
import { useFormatPriceDifferential } from 'components/common/hooks/useFormatPriceDifferential'
import { useClickablePopover } from 'components/common/slider/hooks/useClickablePopover'
import {
  CREATE_CASH_CREDIT_DIFFERENTIAL_MUTATION,
  DELETE_CASH_CREDIT_DIFFERENTIAL_MUTATION,
} from 'components/station/components/competitor-pricing/CompetitorPricingMutations'
import { COMPETITOR_PRICING_QUERY } from 'components/station/components/competitor-pricing/CompetitorPricingQueries'
import classNames from 'classnames/bind'
import styles from './CashCreditDifferentialPopover.module.scss'

const { ALL, CASH, CREDIT } = PricingTypes

const cx = classNames.bind(styles)

const validDifferential = (value, cashDifferential) => {
  if (value === cashDifferential || value === 0) {
    return false
  }
  return true
}

const CashCreditDifferentialPopover = ({
  children,
  stationId,
  fuelGradeId,
  cashCreditDifferential,
  sliderDispatch,
}) => {
  const timestamp = useReactiveVar(timestampVar)
  const { t } = useTranslation()
  const { formatPriceDifferential } = useFormatPriceDifferential()
  const dispatch = usePriceChangeDispatch()
  const [value, setValue] = useState(cashCreditDifferential?.differential || 1)
  const [spreadEnabled, setSpreadEnabled] = useState(
    Boolean(cashCreditDifferential)
  )
  const popoverRef = 'cash-credit-differential-popover-ref'
  useClickablePopover(popoverRef, sliderDispatch)

  const queryVariables = {
    stationId,
    supportedPricingTypes: [ALL, CASH, CREDIT],
    timestamp,
  }

  const writeCashCreditDifferentials = (cache, buildNext) => {
    const {
      station,
      station: {
        priceDifferentials: { fuelGradeDifferentials, cashCreditDifferentials },
      },
    } = cache.readQuery({ query: COMPETITOR_PRICING_QUERY, variables: queryVariables })

    const updatedStation = {
      ...station,
      priceDifferentials: {
        fuelGradeDifferentials,
        cashCreditDifferentials: buildNext(cashCreditDifferentials),
        __typename: 'PriceDifferentialsSet',
      },
    }
    cache.writeQuery({
      query: COMPETITOR_PRICING_QUERY,
      variables: queryVariables,
      data: { station: updatedStation },
    })
  }

  const [createCashCreditDifferential] = useMutation(
    CREATE_CASH_CREDIT_DIFFERENTIAL_MUTATION,
    {
      update(
        cache,
        {
          data: {
            createCashCreditDifferential: { error, cashCreditDifferential },
          },
        }
      ) {
        /* istanbul ignore next */
        if (error) {
          return
        }

        writeCashCreditDifferentials(cache, existing => [
          ...existing.filter(
            differential =>
              differential.fuelGradeId !== cashCreditDifferential.fuelGradeId
          ),
          cashCreditDifferential,
        ])
      },
      onCompleted() {
        document.body.click()
      },
    }
  )

  const [deleteCashCreditDifferential] = useMutation(
    DELETE_CASH_CREDIT_DIFFERENTIAL_MUTATION,
    {
      update(cache, { data: { deleteCashCreditDifferential: { error } } }) {
        /* istanbul ignore next */
        if (error) {
          return
        }

        writeCashCreditDifferentials(cache, existing =>
          existing.filter(
            differential => differential.fuelGradeId !== fuelGradeId
          )
        )
      },
      onCompleted() {
        document.body.click()
      },
    }
  )

  const differentialStep = determineDifferentialStep()

  const handleMinusClick = () => {
    if (value > 1) {
      setValue(numberToFixed(value - differentialStep))
    }
  }

  const handlePlusClick = () => {
    setValue(numberToFixed(value + differentialStep))
  }

  const disabled = !validDifferential(
    value,
    cashCreditDifferential?.differential
  )

  const handleSubmitClick = () => {
    if (!disabled) {
      sliderDispatch({
        type: 'enabled',
        payload: true,
      })
      createCashCreditDifferential({
        variables: {
          stationId,
          fuelGradeId,
          differential: value,
        },
      })
      // Apply the difference to the cash price as a pending change so the on-screen gap
      // reflects it straight away: keep the credit price and reduce the cash price
      // (cash = credit − difference). The user still sends it via the normal confirm flow.
      dispatch({
        type: PriceChangeActions.ApplyCashCreditDifference,
        payload: { fuelGradeId, difference: value },
      })
    }
  }

  const handleToggle = () => {
    if (spreadEnabled) {
      setSpreadEnabled(false)
      if (cashCreditDifferential) {
        sliderDispatch({
          type: 'enabled',
          payload: true,
        })
        deleteCashCreditDifferential({
          variables: { stationId, fuelGradeId },
        })
      }
    } else {
      setSpreadEnabled(true)
    }
  }

  const overlay = (
    <Popover id={popoverRef} className={cx('cash-credit-differential-popover')}>
      <Popover.Content className={cx('content')}>
        <div className={cx('header')}>
          <span className={cx('title')}>
            {t('widgets.competitorPricing.cashCreditDifference')}
          </span>
          <Toggle
            id="cash-credit-spread-toggle"
            label=""
            checked={spreadEnabled}
            handleToggle={handleToggle}
          />
        </div>
        {spreadEnabled && (
          <>
            <div className={cx('value')}>{formatPriceDifferential(value, 0)}</div>
            <div className={cx('buttons')}>
              <div
                className={cx('minus')}
                onClick={handleMinusClick}
                data-testid="minus"
              >
                <MinusIcon />
              </div>
              <div
                className={cx('plus')}
                onClick={handlePlusClick}
                data-testid="plus"
              >
                <PlusIcon />
              </div>
            </div>
            <button
              className={cx('btn', { disabled })}
              onClick={handleSubmitClick}
            >
              {t('widgets.competitorPricing.save')}
            </button>
          </>
        )}
      </Popover.Content>
    </Popover>
  )

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      overlay={overlay}
      rootClose
    >
      {children}
    </OverlayTrigger>
  )
}

CashCreditDifferentialPopover.propTypes = {
  children: PropTypes.node.isRequired,
  stationId: PropTypes.string.isRequired,
  fuelGradeId: PropTypes.string.isRequired,
  cashCreditDifferential: PropTypes.shape(),
  sliderDispatch: PropTypes.func.isRequired,
}

CashCreditDifferentialPopover.defaultProps = {
  cashCreditDifferential: null,
}

export { CashCreditDifferentialPopover }
